import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IconFolder, IconEye } from './Icons';

const EditingScreen = ({ projectData, onComplete, onBack, onError, apiBaseUrl }) => {
  const [categories, setCategories] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [categoryKeywords, setCategoryKeywords] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewResults, setPreviewResults] = useState([]);

  // Инициализация категорий из кластеров
  useEffect(() => {
    if (projectData?.clusters && Object.keys(categories).length === 0) {
      initializeCategoriesFromClusters();
    }
  }, [projectData]);

  const initializeCategoriesFromClusters = () => {
    const initialCategories = {};
    const clustersSource = projectData.clusters || projectData.project?.clusters || {};
    
    Object.entries(clustersSource).forEach(([clusterId, clusterData], index) => {
      const categoryId = `cat_${Date.now()}_${clusterId}`;
      initialCategories[categoryId] = {
        id: categoryId,
        name: clusterData.suggested_name || `Категория ${clusterId}`,
        clusters: [parseInt(clusterId)],
        keywords: clusterData.top_ngrams.slice(0, 5).map(ngram => ngram.ngram),
        description: '',
        color: getCategoryColor(index)
      };
    });

    setCategories(initialCategories);
    
    // Выбираем первую категорию для редактирования
    const firstCategoryId = Object.keys(initialCategories)[0];
    if (firstCategoryId) {
      setActiveCategory(firstCategoryId);
      updateSelectedClusters(initialCategories[firstCategoryId].clusters);
    }
  };

  // Гармоничная палитра категорий: тёмные сине-бирюзовые тона,
  // на которых читается белый текст. Цвет назначается по порядку, а не случайно.
  const getCategoryColor = (index) => {
    const colors = [
      '#007bff', '#0097a7', '#00897b', '#43a047', '#1565c0',
      '#546e7a', '#00838f', '#2e7d32', '#3d7bc4', '#37474f'
    ];
    return colors[index % colors.length];
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) {
      onError('Введите название категории');
      return;
    }

    const categoryId = `cat_${Date.now()}`;
    const newCategory = {
      id: categoryId,
      name: newCategoryName,
      clusters: [],
      keywords: [],
      description: '',
      color: getCategoryColor(Object.keys(categories).length)
    };

    setCategories(prev => ({
      ...prev,
      [categoryId]: newCategory
    }));

    setActiveCategory(categoryId);
    setSelectedClusters([]);
    setNewCategoryName('');
  };

  const handleUpdateCategory = (categoryId, updates) => {
    setCategories(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        ...updates
      }
    }));
  };

  const handleDeleteCategory = (categoryId) => {
    const newCategories = { ...categories };
    delete newCategories[categoryId];
    
    setCategories(newCategories);
    
    // Если удалили активную категорию, выбираем следующую
    if (activeCategory === categoryId) {
      const remainingIds = Object.keys(newCategories);
      if (remainingIds.length > 0) {
        setActiveCategory(remainingIds[0]);
        updateSelectedClusters(newCategories[remainingIds[0]].clusters);
      } else {
        setActiveCategory(null);
        setSelectedClusters([]);
      }
    }
  };

  const handleClusterToggle = (clusterId) => {
    if (activeCategory) {
      const currentClusters = [...(categories[activeCategory]?.clusters || [])];
      
      const updatedClusters = currentClusters.includes(clusterId)
        ? currentClusters.filter(id => id !== clusterId)
        : [...currentClusters, clusterId];
      
      handleUpdateCategory(activeCategory, { clusters: updatedClusters });
      updateSelectedClusters(updatedClusters);
    }
  };

  const updateSelectedClusters = (clusterIds) => {
    setSelectedClusters(clusterIds);
    
    // Обновляем предпросмотр
    if (clusterIds.length > 0) {
      updatePreview(clusterIds);
    }
  };

  const updatePreview = async (clusterIds) => {
    setLoading(true);
    try {
      const clustersSource = projectData.clusters || projectData.project?.clusters || {};
      const previewExamples = [];
      
      clusterIds.forEach(clusterId => {
        const cluster = clustersSource[clusterId];
        if (cluster && cluster.examples) {
          cluster.examples.forEach((example, idx) => {
            if (previewExamples.length < 5) {
              previewExamples.push({
                text: example,
                clusterId: clusterId,
                category: categories[activeCategory]?.name || 'Без категории'
              });
            }
          });
        }
      });
      
      setPreviewResults(previewExamples);
    } catch (err) {
      console.error('Preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddKeyword = (categoryId, keyword) => {
    if (!keyword.trim()) return;
    
    const currentKeywords = categories[categoryId]?.keywords || [];
    if (!currentKeywords.includes(keyword)) {
      handleUpdateCategory(categoryId, {
        keywords: [...currentKeywords, keyword]
      });
    }
  };

  const handleRemoveKeyword = (categoryId, keyword) => {
    const currentKeywords = categories[categoryId]?.keywords || [];
    handleUpdateCategory(categoryId, {
      keywords: currentKeywords.filter(k => k !== keyword)
    });
  };

  const handleSaveAndContinue = async () => {
    setLoading(true);
    try {
      // Сохраняем категории на бэкенде
      const response = await axios.post(
        `${apiBaseUrl}/api/project/${projectData.project_id}/update_categories`,
        { categories }
      );

      onComplete({
        ...categories,
        classification: response.data.classification
      });
    } catch (err) {
      onError(err.response?.data?.error || 'Ошибка при сохранении категорий');
    } finally {
      setLoading(false);
    }
  };

  const handleMergeCategories = (sourceId, targetId) => {
    if (sourceId === targetId) return;

    const source = categories[sourceId];
    const target = categories[targetId];

    const mergedCategory = {
      id: targetId,
      name: target.name,
      clusters: [...new Set([...target.clusters, ...source.clusters])],
      keywords: [...new Set([...target.keywords, ...source.keywords])],
      description: target.description || source.description,
      color: target.color
    };

    // Обновляем целевую категорию и удаляем исходную
    const newCategories = { ...categories };
    newCategories[targetId] = mergedCategory;
    delete newCategories[sourceId];

    setCategories(newCategories);
    
    if (activeCategory === sourceId) {
      setActiveCategory(targetId);
      updateSelectedClusters(mergedCategory.clusters);
    }
  };

  return (
    <div className="editing-screen">
      <div className="editing-header">
        <h2>3. Редактирование категорий</h2>
        <p className="subtitle">
          Назначьте кластеры категориям и добавьте ключевые слова для точной классификации
        </p>
      </div>

      <div className="editing-layout">
        {/* Левая колонка - список категорий */}
        <div className="categories-sidebar">
          <div className="add-category-form">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Новая категория..."
              onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
            />
            <button onClick={handleAddCategory} className="add-button">
              + Добавить
            </button>
          </div>

          <div className="categories-list">
            {Object.values(categories).map(category => (
              <div
                key={category.id}
                className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategory(category.id);
                  updateSelectedClusters(category.clusters);
                }}
                style={{ borderLeftColor: category.color }}
              >
                <div className="category-header">
                  <div className="category-name">{category.name}</div>
                  <button
                    className="delete-category-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCategory(category.id);
                    }}
                    title="Удалить категорию"
                  >
                    ×
                  </button>
                </div>
                <div className="category-meta">
                  <span className="cluster-count">
                    {category.clusters.length} кластеров
                  </span>
                  <span className="keyword-count">
                    {category.keywords.length} ключевых слов
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Центральная колонка - редактирование категории */}
        <div className="category-editor">
          {activeCategory && categories[activeCategory] ? (
            <>
              <div className="editor-header">
                <h3>Редактирование: {categories[activeCategory].name}</h3>
                <div
                  className="category-color"
                  style={{ backgroundColor: categories[activeCategory].color }}
                />
              </div>

              <div className="editor-section">
                <label>Название категории:</label>
                <input
                  type="text"
                  value={categories[activeCategory].name}
                  onChange={(e) => handleUpdateCategory(activeCategory, { name: e.target.value })}
                  className="category-name-input"
                />
              </div>

              <div className="editor-section">
                <label>Описание (опционально):</label>
                <textarea
                  value={categories[activeCategory].description || ''}
                  onChange={(e) => handleUpdateCategory(activeCategory, { 
                    description: e.target.value 
                  })}
                  placeholder="Добавьте описание категории..."
                  rows="3"
                />
              </div>

              <div className="editor-section">
                <label>Ключевые слова:</label>
                <div className="keywords-input-container">
                  <input
                    type="text"
                    placeholder="Добавить ключевое слово..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        handleAddKeyword(activeCategory, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="keyword-input"
                  />
                  <div className="keywords-list">
                    {categories[activeCategory].keywords.map((keyword, idx) => (
                      <span key={idx} className="keyword-tag">
                        {keyword}
                        <button
                          onClick={() => handleRemoveKeyword(activeCategory, keyword)}
                          className="remove-keyword-btn"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="editor-section">
                <label>Присвоенные кластеры ({selectedClusters.length}):</label>
                <div className="clusters-selection">
                  {Object.entries(projectData.clusters || projectData.project?.clusters || {}).map(([clusterId, clusterData]) => (
                    <div
                      key={clusterId}
                      className={`cluster-selector ${
                        selectedClusters.includes(parseInt(clusterId)) ? 'selected' : ''
                      }`}
                      onClick={() => handleClusterToggle(parseInt(clusterId))}
                    >
                      <div className="cluster-selector-header">
                        <div className="selector-checkbox">
                          {selectedClusters.includes(parseInt(clusterId)) ? '✓' : ''}
                        </div>
                        <div className="selector-name">
                          Кластер {parseInt(clusterId) + 1}: {clusterData.suggested_name}
                        </div>
                      </div>
                      <div className="selector-size">
                        {clusterData.size} текстов
                      </div>
                      <div className="selector-keywords">
                        {clusterData.top_ngrams.slice(0, 3).map(ngram => (
                          <span key={ngram.ngram} className="selector-keyword">
                            {ngram.ngram}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Объединение категорий */}
              <div className="editor-section">
                <label>Объединение категорий:</label>
                <div className="merge-categories">
                  <select
                    className="merge-select"
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value && e.target.value !== activeCategory) {
                        handleMergeCategories(activeCategory, e.target.value);
                      }
                    }}
                  >
                    <option value="">Объединить с...</option>
                    {Object.values(categories)
                      .filter(cat => cat.id !== activeCategory)
                      .map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </>
          ) : (
            <div className="no-category-selected">
              <div className="empty-state">
                <div className="empty-icon"><IconFolder size={48} /></div>
                <h3>Выберите категорию для редактирования</h3>
                <p>Или создайте новую категорию в левой панели</p>
              </div>
            </div>
          )}
        </div>

        {/* Правая колонка - предпросмотр */}
        <div className="preview-sidebar">
          <h3>Предпросмотр классификации</h3>
          
          {loading ? (
            <div className="preview-loading">
              <div className="spinner"></div>
              <p>Обновление предпросмотра...</p>
            </div>
          ) : previewResults.length > 0 ? (
            <>
              <div className="preview-stats">
                <div className="preview-stat">
                  <div className="stat-value">{selectedClusters.length}</div>
                  <div className="stat-label">кластеров</div>
                </div>
                <div className="preview-stat">
                  <div className="stat-value">
                    {selectedClusters.reduce((total, clusterId) => 
                      total + (projectData.project?.clusters[clusterId]?.size || 0), 0
                    )}
                  </div>
                  <div className="stat-label">текстов</div>
                </div>
              </div>

              <div className="preview-examples">
                <h4>Примеры текстов:</h4>
                {previewResults.map((item, idx) => (
                  <div key={idx} className="preview-example">
                    <div className="example-text">{item.text}</div>
                    <div className="example-meta">
                      <span className="cluster-badge">Кластер {parseInt(item.clusterId) + 1}</span>
                      <span className="category-badge" style={{ 
                        backgroundColor: categories[activeCategory]?.color || '#5cb85c'
                      }}>
                        {item.category}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="preview-empty">
              <div className="empty-icon"><IconEye size={48} /></div>
              <p>Выберите кластеры для предпросмотра</p>
            </div>
          )}
        </div>
      </div>

      <div className="editing-footer">
        <div className="categories-summary">
          Создано категорий: {Object.keys(categories).length} | 
          Всего кластеров: {Object.keys(projectData.clusters || projectData.project?.clusters || {}).length}
        </div>
        
        <div className="action-buttons">
          <button className="secondary-button" onClick={onBack}>
            ← Назад к кластеризации
          </button>
          <button 
            className="primary-button"
            onClick={handleSaveAndContinue}
            disabled={Object.keys(categories).length === 0 || loading}
          >
            {loading ? 'Сохранение...' : 'Сохранить и продолжить →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditingScreen;
